import Image from 'next/image';
import { Icon } from '../Icon';

export interface SpecialOfferItem {
  id: number | string;
  title: string;
  description: string | null;
  imagePath: string | null;
  expiresAt: Date | null;
}

export interface VBCSpecialOffersProps {
  offers: SpecialOfferItem[];
}

function formatDate(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Special offers section. Server Component.
 * Uses CSS variables with fallback values for theming.
 */
export function VBCSpecialOffers({ offers }: VBCSpecialOffersProps) {
  if (offers.length === 0) return null;

  return (
    <section
      className="mb-10 rounded-xl border p-6"
      style={{
        borderColor: 'var(--brand-card-border)',
        boxShadow: 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      <h2
        className="overline mb-5"
        style={{ color: 'var(--brand-primary, #014F8A)' }}
      >
        Special Offers
      </h2>
      <div className="space-y-4">
        {offers.map((offer) => (
          <div
            key={offer.id}
            className="rounded-xl border p-5"
            style={{
              borderColor: 'var(--brand-secondary, #FFD700)',
              backgroundColor: 'color-mix(in srgb, var(--brand-secondary, #FFD700) 6%, transparent)',
            }}
          >
            <div className="flex items-start gap-4">
              {offer.imagePath && (
                <Image
                  src={offer.imagePath}
                  alt="Offer image"
                  width={72}
                  height={72}
                  className="h-[72px] w-[72px] flex-shrink-0 rounded-xl object-cover"
                  style={{
                    border: '1px solid var(--brand-card-border)',
                  }}
                />
              )}
              <div className="flex-1">
                <h3
                  className="font-semibold"
                  style={{
                    color: 'var(--brand-text)',
                    fontSize: '15px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {offer.title}
                </h3>
                {offer.description && (
                  <p
                    className="mt-1.5"
                    style={{
                      color: 'var(--brand-text-muted)',
                      fontSize: '13px',
                      lineHeight: 1.6,
                      letterSpacing: '0.01em',
                    }}
                  >
                    {offer.description}
                  </p>
                )}
                {offer.expiresAt && (
                  <p
                    className="mt-2.5 flex items-center gap-1.5"
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '12px',
                      letterSpacing: '0.01em',
                    }}
                  >
                    <Icon name="calendar_today" size={12} />
                    Valid through {formatDate(offer.expiresAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
